export class OrderConfirmationModal {
  constructor(config = null, paymentService = null, i18n = null) {
    this.config = config;
    this.paymentService = paymentService;
    this.i18n = i18n;
    this.isVisible = false;
    this.orderItems = [];
    this.orderTotals = {
      subtotal: 0,
      total: 0
    };
    this.estimatedTime = '15-20 minutes';
    this.selectedPaymentMethod = null;
    this.availablePaymentMethods = {};
    this.isLoadingPaymentMethods = false;
  }

  async show(orderItems, orderTotals = {}) {
    if (this.isVisible) {
      return;
    }

    const timing = this.config?.paymentFlowConfiguration?.paymentTiming;
    const isBeforeOrder = (timing ? timing === 'BEFORE_ORDER' : true);
    if (isBeforeOrder) {
      return;
    }

    this.orderItems = orderItems || [];
    const subtotal = this.parseAmount(orderTotals.subtotal);
    const total = this.parseAmount(orderTotals.total ?? orderTotals.subtotal);
    this.orderTotals = {
      subtotal,
      total: total ?? subtotal ?? 0
    };
    this.isVisible = true;

    this.render();
    this.bindEvents();

    document.body.style.overflow = 'hidden';
  }

  parseAmount(value) {
    if (value == null) {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  t(key, fallback) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback;
    }
    const translation = this.i18n.t(key);
    return translation === key ? fallback : translation;
  }

  formatPrice(amount, currencyOverride = null) {
    const currency = currencyOverride || this.getCurrency();
    const numericAmount = Number(amount);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    return `${safeAmount.toFixed(2)} ${currency}`;
  }

  hide() {
    console.log('[OrderConfirmationModal] Hide method called');
    this.isVisible = false;
    const modal = document.getElementById('order-confirmation-modal');
    if (modal) {
      console.log('[OrderConfirmationModal] Removing modal from DOM');
      modal.remove();
    } else {
      console.log('[OrderConfirmationModal] Modal not found in DOM');
    }

    if (this.boundHandleKeyDown) {
      document.removeEventListener('keydown', this.boundHandleKeyDown);
    }
    if (this.boundHandleDocumentClick) {
      document.removeEventListener('click', this.boundHandleDocumentClick);
    }

    document.body.style.overflow = '';
  }

  render() {
    const modalHtml = `
      <div id="order-confirmation-modal" class="order-confirmation-container" role="dialog" aria-modal="true">
        <div class="modal-overlay"></div>
        <div class="modal order-modal">
          <div class="modal-header">
            <h2 class="modal-title">
              ${this.t('order.confirmTitle', 'Confirm Your Order')}
            </h2>
            <button class="modal-close" id="order-modal-close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="modal-body two-col">
            <div class="left-panel">
              <div class="order-summary">
                <div class="summary-header">
                  <h3 class="summary-title">${this.t('order.summaryTitle', 'Order Summary')}</h3>
                </div>
                <div class="items-scroll" role="list">
                  <div class="items-list">
                  ${this.renderOrderItems()}
                  </div>
                </div>
              </div>
              <div class="order-totals">
                ${this.renderTotals()}
              </div>
            </div>
            <div class="right-panel">
              <div class="actions">
                <button class="btn btn-primary" id="confirm-pay-now-btn">
                  <i class="fas fa-check"></i>
                  ${this.t('order.payNow', 'Confirm & Pay Now')}
                </button>
                <button class="btn btn-secondary" id="confirm-order-btn">
                  ${this.t('order.payLater', 'Confirm (Pay Later)')}
                </button>
              </div>
              <div class="action-note">${this.t('order.paymentNote', 'You can pay online, at the bar, or with card/cash at the table.')}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  renderOrderItems() {
    return this.orderItems.map((item, index) => {
      const imageSrc = item.image || '';
      const totalPrice = this.resolveItemTotal(item);
      const itemCurrency = item.currency || this.getCurrency();

      return `
        <div class="item" data-item-index="${index}" role="listitem">
        ${imageSrc ?
            `<img src="${imageSrc}"
                 alt="${item.name}"
                 class="item-image">` :
            `<div class="item-image item-image-placeholder">
              <i class="fas fa-utensils"></i>
            </div>`
          }
        <div class="item-details">
          <div class="item-header">
            <h4 class="item-title">${item.name}</h4>
            <span class="item-price">${this.formatPrice(totalPrice, itemCurrency)}</span>
          </div>
          <p class="item-mods">${this.formatModifiers(item.modifiers)}</p>
        </div>
        <span class="item-quantity">×${item.quantity}</span>
      </div>
      `;
    }).join('');
  }

  formatModifiers(modifiers) {
    if (!modifiers || Object.keys(modifiers).length === 0) {
      return this.t('order.noModifications', 'No modifications');
    }

    const modStrings = Object.entries(modifiers).map(([groupId, optionId]) => {
      return `${groupId}: ${optionId}`;
    });

    const formatted = modStrings.join(', ');
    return formatted || this.t('order.noModifications', 'No modifications');
  }

  renderMessageBox() { return `` }

  renderTotals() {
    return `
      <div class="total-row grand-total">
        <span>${this.t('order.totalLabel', 'Total')}</span>
        <span>${this.formatPrice(this.orderTotals.total)}</span>
      </div>
    `;
  }

  resolveItemTotal(item) {
    if (item == null) {
      return 0;
    }
    if (item.totalPrice != null && !Number.isNaN(Number(item.totalPrice))) {
      return Number(item.totalPrice);
    }
    const price = Number(item.price ?? item.unitPrice ?? 0);
    const quantity = Number(item.quantity ?? 1);
    if (Number.isNaN(price) || Number.isNaN(quantity)) {
      return 0;
    }
    return price * quantity;
  }

  renderEstimatedTime() {
    return ``;
  }

  async loadPaymentMethods() { return }

  renderPaymentMethods() { return '' }

  getPaymentMethodDescription(method) {
    switch (method.id) {
      case 'card':
        return `Tap or insert your card`;
      case 'qr_code':
        return `Scan QR with your phone`;
      case 'mobile_redirect':
        return `Complete payment on phone`;
      case 'cash':
        return `Pay at the counter`;
      default:
        return '';
    }
  }

  getConfirmButtonText() {
    if (!this.selectedPaymentMethod) {
      return this.t('order.selectPaymentMethod', 'Select Payment Method');
    }
    return this.selectedPaymentMethod === 'cash'
      ? this.t('order.placeOrder', 'Place Order')
      : this.t('order.proceedToPayment', 'Proceed to Payment');
  }

  refreshPaymentMethods() {
    const paymentSection = document.querySelector('#order-confirmation-modal .payment-section');
    if (paymentSection) {
      paymentSection.innerHTML = `
        <h3 class="payment-title">Payment Method</h3>
        ${this.renderPaymentMethods()}
      `;
    }

    const confirmBtn = document.getElementById('confirm-order-btn');
    if (confirmBtn) {
      confirmBtn.disabled = !this.selectedPaymentMethod;
      confirmBtn.textContent = this.getConfirmButtonText();
    }
  }

  bindEvents() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);

    document.addEventListener('keydown', this.boundHandleKeyDown);
    document.addEventListener('click', this.boundHandleDocumentClick);
  }

  handleKeyDown(e) {
    if (this.isVisible && e.key === 'Escape') {
      this.hide();
    }
  }

  handleDocumentClick(e) {
    if (!this.isVisible) return;

    if (e.target.id === 'order-modal-close' || e.target.closest('#order-modal-close') ||
        e.target.classList.contains('modal-overlay')) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
      return;
    }

    if (e.target.id === 'confirm-order-btn') {
      e.preventDefault();
      e.stopPropagation();
      this.handleConfirmLater();
      return;
    }

    if (e.target.id === 'confirm-pay-now-btn') {
      e.preventDefault();
      e.stopPropagation();
      this.handleConfirmPayNow();
      return;
    }
  }

  handleQuantityChange(itemIndex, change) {
    if (itemIndex < 0 || itemIndex >= this.orderItems.length) return;

    const item = this.orderItems[itemIndex];
    const newQuantity = item.quantity + change;

    if (newQuantity < 1) {
      this.handleRemoveItem(itemIndex);
      return;
    }

    item.quantity = newQuantity;
    item.totalPrice = item.price * newQuantity;

    this.updateTotals();

    this.refreshModal();
  }

  handleRemoveItem(itemIndex) {
    if (itemIndex < 0 || itemIndex >= this.orderItems.length) return;

    this.orderItems.splice(itemIndex, 1);

    if (this.orderItems.length === 0) {
      this.hide();
      return;
    }

    this.updateTotals();

    this.refreshModal();
  }

  updateTotals() {
    const subtotal = this.orderItems.reduce((sum, item) => {
      return sum + this.resolveItemTotal(item);
    }, 0);

    const total = subtotal;

    this.orderTotals = {
      subtotal,
      total
    };
  }

  refreshModal() {
    const itemsList = document.querySelector('#order-confirmation-modal .items-list');
    if (itemsList) {
      itemsList.innerHTML = this.renderOrderItems();
    }

    const orderTotals = document.querySelector('#order-confirmation-modal .order-totals');
    if (orderTotals) {
      orderTotals.innerHTML = this.renderTotals() + this.renderEstimatedTime();
    }
  }

  selectPaymentMethod(methodId) {
    if (this.selectedPaymentMethod === methodId) return;

    this.selectedPaymentMethod = methodId;
    this.refreshPaymentMethods();

    console.log('[OrderModal] Payment method selected:', methodId);

    const event = new CustomEvent('beforeOrderMethodSelected', {
      detail: {
        paymentMethod: methodId,
        orderItems: this.orderItems,
        orderTotals: this.orderTotals
      }
    });
    document.dispatchEvent(event);

    this.hide();
  }

  handleConfirmLater() {
    const event = new CustomEvent('confirmOrder', {
      detail: {
        orderItems: this.orderItems,
        orderTotals: this.orderTotals,
        estimatedTime: this.estimatedTime,
        paymentMethod: 'cash'
      }
    });
    document.dispatchEvent(event);
    this.hide();
  }

  handleConfirmPayNow() {
    const event = new CustomEvent('confirmOrder', {
      detail: {
        orderItems: this.orderItems,
        orderTotals: this.orderTotals,
        estimatedTime: this.estimatedTime,
        paymentMethod: 'qr_code'
      }
    });
    document.dispatchEvent(event);
    this.hide();
  }

  getCurrency() {
    if (this.orderItems && this.orderItems.length > 0 && this.orderItems[0].currency) {
      return this.orderItems[0].currency;
    }
    return this.config?.settings?.currency || this.config?.currency || 'PLN';
  }

  static calculateTotals(orderItems) {
    const subtotal = orderItems.reduce((sum, item) => {
      return sum + (item.totalPrice || (item.price * item.quantity));
    }, 0);

    const total = subtotal;

    return {
      subtotal,
      total
    };
  }
}
